import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banknote, Check, ChefHat, Clock, PackageCheck, Plus, RefreshCw, ShoppingBag } from 'lucide-react';
import type { PedidoVista } from '@shared/types';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { CobroModal } from '@renderer/components/cobro/CobroModal';
import { CobrarDiferenciaModal } from '@renderer/components/cobro/CobrarDiferenciaModal';
import { TicketPreviewModal } from '@renderer/components/ticket/TicketPreviewModal';

/**
 * Sprint 108 — Venta de mostrador: el cliente llega, pide para llevar y espera.
 *
 * ## El hueco que cierra
 *
 * El Sprint 104 agregó el tipo de venta… y no le dio puerta: el Dashboard entra al POS con
 * `?tipo=comer_aqui` predefinido, así que el selector de tres tarjetas **nunca llegaba a
 * mostrarse**. La pregunta del dueño del producto fue exacta: *"¿dónde va a registrar venta de
 * mostrador?"*. La respuesta honesta era **en ninguna parte**.
 *
 * ## El flujo, dicho por él
 *
 * *"¿A nombre de quién? · ¿Qué desea ordenar? · Se le indica el monto · Se cobra y se emite el
 * ticket para el cliente con su folio y el de cocina · El flujo pasa a EN COCINA y LISTO
 * EMPAQUETADO · Se le entrega y se da clic en ENTREGADO."*
 *
 * **Hay que ver quién está esperando.** Sin esta pantalla, los pedidos de mostrador se llevan de
 * memoria — y quien atiende sola no tiene memoria libre.
 *
 * Es hermana de `A domicilio`: mismo patrón de tablero, columnas distintas porque los problemas
 * son distintos. El domicilio se define por sus esperas de terceros (moto, transferencia); el
 * mostrador no tiene ninguna: sólo la cocina y el cliente parado ahí.
 *
 * ---
 *
 * ## Sprint 111 — Se corrige el orden, porque lo dijo la dueña
 *
 * Aquí decía *"se cobra ANTES de cocinar: el cliente está de pie con la cartera en la mano"*. Era
 * una suposición nuestra, escrita como si fuera del oficio. Se le preguntó a quien atiende el
 * mostrador y contestó: **"Primero se surte el pedido."**
 *
 * Y el argumento tampoco se sostenía por otro lado: aquí **todo se vende ya cocinado** (DT-102), y
 * si el cliente se arrepiente *"se le hace la devolución y el producto no se pierde"* — vuelve a la
 * vitrina. Cobrar por adelantado no protegía de nada.
 *
 * El flujo real, y el de esta pantalla: **se toma → se surte → se cobra → se entrega.** La única
 * regla que sí es suya la hace cumplir el servidor: *"no se lo entrego hasta que lo pague"*.
 *
 * Y las tres cosas se hacen **desde aquí**, sin cambiar de sección: quien atiende sola no puede
 * estar yendo a la pantalla de Cocina para dar un paso y volver.
 */
export function MostradorPage() {
  const navigate = useNavigate();
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const [pedidos, setPedidos] = useState<PedidoVista[]>([]);
  const [cargando, setCargando] = useState(true);
  const [ahora, setAhora] = useState(() => new Date());
  /**
   * Sprint 111 — El cobro vive aquí, que es donde está el cliente.
   *
   * Desde que la dueña dijo *"primero se surte el pedido"*, éste es **el** lugar donde se cobra:
   * cuando la bolsa ya está lista y ella la tiene enfrente. Antes había que buscar el pedido en
   * la sección de Pedidos para cobrarlo, o cobrarlo a ciegas al capturar.
   */
  const [cobroId, setCobroId] = useState<number | null>(null);
  /** Sprint 141 — El pedido al que se le agregó algo después de cobrarle. */
  const [diferenciaId, setDiferenciaId] = useState<number | null>(null);
  /**
   * Sprint 112 — El ticket, que aquí no existía.
   *
   * El Sprint 111 trajo el cobro a esta pantalla y la volvió **el** camino normal —desde que la
   * dueña dijo "primero se surte", es aquí donde se cobra casi todo— pero se le olvidó el paso
   * siguiente: el botón dice *"Cobrar y emitir ticket"* y no emitía ninguno. Se cobraba, el modal
   * se cerraba, y el cliente se quedaba parado sin comprobante y sin forma de pedir uno.
   *
   * Verificado en producción el 2026-08-26: la venta #00007 entró al corte correctamente y **nunca
   * apareció el ticket**. El sprint anterior había arreglado ese modal a fondo (método de pago,
   * encabezado, botón "Listo", timeout de impresora) — pero en `PosPage`, la puerta vieja. Es el
   * patrón de "media puerta cerrada": la regla se aplicó en una de las dos entradas.
   */
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [previewPdfPath, setPreviewPdfPath] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    /**
     * Sprint 111 — Se pregunta por la **entrega**, no por el estado.
     *
     * Antes esto pedía `listarPorEstado(['pendiente_cocina','cocina','listo'])` y filtraba aquí.
     * Parecía correcto y tenía un agujero: el cobro anticipado que introdujo el Sprint 108 deja
     * el pedido en `'pagado'` —terminal en este sistema— así que **la tarjeta desaparecía del
     * tablero en el mismo instante en que se cobraba**. El pedido existía, el dinero estaba en el
     * corte, y en la pantalla no había nada: nadie lo armaba y el cliente seguía parado.
     *
     * Textual del dueño (2026-08-26): *"no envió nada a cocina, es literalmente como si no
     * hubiese habido venta"*.
     */
    const r = await window.api.pedidos.listarMostrador();
    if (r.ok) setPedidos(r.data);
    setCargando(false);
  }, []);

  useEffect(() => {
    void cargar();
    const t = setInterval(() => void cargar(), 15000);
    return () => clearInterval(t);
  }, [cargar]);

  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  /**
   * Sprint 111 — Las columnas se dividen por **si la bolsa ya está armada**, no por estado.
   *
   * Por estado ya no distinguían nada útil: cobrar deja el pedido en `'pagado'` y todos caían del
   * mismo lado. Y el eje que de verdad le importa a quien atiende es otro — *¿esto ya está listo o
   * todavía lo están surtiendo?* — porque es justamente lo que ella ve con los ojos cuando mira
   * hacia la cocina, y lo que ahora puede registrar sin cambiar de pantalla.
   *
   * Se usa `fechaEmpaquetado` (Sprint 66) y no `estado`: es un eje aparte que no pisa de dónde el
   * cierre del día suma las ventas.
   */
  const surtiendo = pedidos.filter((p) => !p.fechaEmpaquetado);
  const listos = pedidos.filter((p) => !!p.fechaEmpaquetado);

  /**
   * Sprint 111 — "Ya está listo", sin salir de esta pantalla.
   *
   * Petición del dueño: *"Claudia está sola y puede ver cuándo la cocina armó el paquete; debería
   * poder mover el flujo sin salir de la sección donde se encuentra"*. Antes había que irse a la
   * pantalla de Cocina para dar ese paso, y volver.
   *
   * Usa `marcar-empaquetado` (el eje `fechaEmpaquetado`, Sprint 66) y **no `marcar-listo`**: éste
   * último escribe `estado: 'listo'`, y si el pedido ya se cobró su estado es `'pagado'` — de ahí
   * suma el cierre del día. Mover ese campo sacaría la venta del corte.
   */
  const marcarListo = async (p: PedidoVista) => {
    const r = await window.api.pedidos.marcarEmpaquetado(p.id);
    if (!r.ok) {
      await window.alertar({
        titulo: 'No se pudo marcar como listo',
        mensaje: traducirErrorBackend(r.error, r.codigo),
        variant: 'warn',
      });
      return;
    }
    void cargar();
  };

  /**
   * "Se lo llevó".
   *
   * Cierra la entrega y saca la tarjeta de la pantalla. No mueve dinero: el cobro ocurrió un paso
   * antes, cuando la bolsa ya estaba lista. El servidor **exige que exista ese pago** antes de
   * dejar entregar — es la regla de la dueña: *"no se lo entrego hasta que lo pague"*.
   */
  const marcarEntregado = async (p: PedidoVista) => {
    const r = await window.api.pedidos.marcarEntregadoMostrador({ pedidoId: p.id });
    if (!r.ok) {
      await window.alertar({
        titulo: 'No se pudo cerrar el pedido',
        mensaje: traducirErrorBackend(r.error, r.codigo),
        variant: 'warn',
      });
      return;
    }
    void cargar();
  };

  const minutos = (iso: string) => {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return 0;
    return Math.max(0, Math.floor((ahora.getTime() - t) / 60000));
  };

  const total = pedidos.reduce((s, p) => s + p.total, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <ShoppingBag className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Venta de mostrador</h1>
            <p className="text-sm text-slate-500">
              {pedidos.length === 0
                ? 'Nadie esperando su pedido.'
                : `${pedidos.length} esperando · ${formatPrecio(total, monedaSimbolo, locale)}`}
            </p>
          </div>
        </div>
        <button onClick={() => void cargar()} className="btn-ghost" aria-label="Refrescar">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/*
        Sprint 108 — El botón grande, arriba.

        Es lo que se hace ciento cincuenta veces al día en un negocio de mostrador: tomar el
        siguiente pedido. Todo lo demás de esta pantalla es consecuencia.
      */}
      <button
        onClick={() => navigate('/pos?tipo=para_llevar&origen=mostrador')}
        className="flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 p-5 text-lg font-bold text-white shadow-lg transition hover:shadow-xl"
      >
        <Plus className="h-6 w-6" />
        Nuevo pedido de mostrador
      </button>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/*
          Sprint 111 — Dos columnas, y **una sola acción por tarjeta**: la que toca ahora.

          El orden lo dio la dueña: *"primero se surte el pedido"*. Así que la primera columna es
          lo que está armándose y la segunda lo que ya está en la bolsa — y ahí, según haya cobro o
          no, el botón dice "Cobrar" o "Se lo llevó". El operador nunca elige entre dos botones:
          lee el que hay.
        */}
        <Columna
          titulo="Surtiendo"
          icono={<ChefHat className="h-4 w-4" />}
          vacia="Nada pendiente de armar."
          cargando={cargando}
          pedidos={surtiendo}
          monedaSimbolo={monedaSimbolo}
          locale={locale}
          minutos={minutos}
          accion={{
            etiqueta: 'Ya está listo',
            icono: <PackageCheck className="h-4 w-4" />,
            onClick: (p) => void marcarListo(p),
          }}
        />

        <Columna
          titulo="Listo — entregar"
          icono={<PackageCheck className="h-4 w-4" />}
          vacia="Nada empaquetado esperando."
          cargando={cargando}
          pedidos={listos}
          monedaSimbolo={monedaSimbolo}
          locale={locale}
          minutos={minutos}
          resaltar
          accion={(p) => {
            /*
              Sprint 141 — Un pedido puede estar cobrado **y deber dinero**.

              Es la escena que abrió el sprint: el cliente ya pagó, está esperando su bolsa y pide
              algo más. Decidir con `yaTienePago` le ofrecía "Se lo llevó" y el cliente salía por
              la puerta con $20 sin cobrar, sin que nada en la pantalla lo dijera. La pregunta
              correcta no es "¿tiene pagos?" sino "¿falta dinero?".
            */
            if ((p.faltaCobrar ?? 0) > 0) {
              return {
                etiqueta: `Cobrar ${formatPrecio(p.faltaCobrar ?? 0, monedaSimbolo, locale)} que falta`,
                icono: <Banknote className="h-4 w-4" />,
                onClick: () => setDiferenciaId(p.id),
              };
            }
            return p.yaTienePago === true
              ? {
                  etiqueta: 'Se lo llevó',
                  icono: <Check className="h-4 w-4" />,
                  onClick: () => void marcarEntregado(p),
                }
              : {
                  // Su regla, y la hace cumplir el servidor: no se entrega sin cobrar.
                  etiqueta: 'Cobrar',
                  icono: <Banknote className="h-4 w-4" />,
                  onClick: () => setCobroId(p.id),
                };
          }}
        />
      </div>

      <CobroModal
        abierto={cobroId !== null}
        pedidoId={cobroId}
        onClose={() => {
          setCobroId(null);
          void cargar();
        }}
        onCobrado={(pdfPath) => {
          // El ticket se abre con el pedido que se acaba de cobrar, no con el que quede después.
          setPreviewPdfPath(pdfPath ?? null);
          setPreviewId(cobroId);
          setCobroId(null);
          void cargar();
        }}
      />

      {/*
        Sprint 141 — Cobrar sólo lo que falta, desde donde está el cliente esperando.

        No sirve el `CobroModal` de arriba: ése cobra el pedido entero y el servidor lo rechaza
        —un pedido se cobra una sola vez, candado del Sprint 69—, así que el operador se quedaría
        sin salida con el cliente delante.
      */}
      {diferenciaId !== null && (
        <CobrarDiferenciaModal
          abierto
          pedidoId={diferenciaId}
          falta={pedidos.find((p) => p.id === diferenciaId)?.faltaCobrar ?? 0}
          // En mostrador no hay puerta que tocar: el cliente está aquí.
          puedeCobrarAlEntregar={false}
          onCerrar={() => setDiferenciaId(null)}
          onCobrado={() => {
            setDiferenciaId(null);
            void cargar();
          }}
        />
      )}

      {/*
        Sprint 112 — Cobrado, el ticket. Mismo componente que usa el POS: uno solo, para que no
        vuelva a arreglarse en una puerta y no en la otra.

        Al cerrarlo se recarga el tablero: la tarjeta sigue ahí —ya pagada— esperando el
        "Se lo llevó", que es el paso que falta.
      */}
      <TicketPreviewModal
        abierto={previewId !== null}
        pedidoId={previewId}
        pdfPath={previewPdfPath}
        onClose={() => {
          setPreviewId(null);
          setPreviewPdfPath(null);
          void cargar();
        }}
      />
    </div>
  );
}

interface Accion {
  etiqueta: string;
  icono: JSX.Element;
  onClick: (p: PedidoVista) => void;
}

function Columna(props: {
  titulo: string;
  icono: JSX.Element;
  vacia: string;
  cargando: boolean;
  pedidos: PedidoVista[];
  monedaSimbolo: string;
  locale: string;
  minutos: (iso: string) => number;
  resaltar?: boolean;
  /**
   * Sprint 111 — La acción puede depender del pedido.
   *
   * En la columna "Listo" no es la misma para todos: al que ya se cobró le toca "Se lo llevó" y al
   * que no, "Cobrar". Se resuelve por tarjeta para que el operador **nunca elija entre dos
   * botones**: lee el único que hay.
   */
  accion: Accion | ((p: PedidoVista) => Accion);
}) {
  return (
    <section className="card overflow-hidden p-0">
      <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          {props.icono}
          {props.titulo}
        </h2>
        <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-600">
          {props.pedidos.length}
        </span>
      </header>
      <div className="space-y-3 p-3">
        {props.cargando ? (
          <p className="py-6 text-center text-sm text-slate-400">Cargando…</p>
        ) : props.pedidos.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">{props.vacia}</p>
        ) : (
          props.pedidos.map((p) => {
            const espera = props.minutos(p.fechaCreacion);
            const accion = typeof props.accion === 'function' ? props.accion(p) : props.accion;
            return (
              <article
                key={p.id}
                className={`rounded-xl border p-3 ${
                  props.resaltar ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-lg font-bold">
                      #{String(p.numeroPedido).padStart(5, '0')}
                    </div>
                    {/*
                      El nombre en grande: es cómo se llama al cliente cuando su bolsa está lista.
                      Un folio sin nombre obliga a gritar un número, y nadie levanta la vista.
                    */}
                    <div className="truncate text-base font-semibold text-slate-800">
                      {p.clienteNombre?.trim() || 'Sin nombre'}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-mono font-bold">
                      {formatPrecio(p.total, props.monedaSimbolo, props.locale)}
                    </div>
                    <div
                      className={`flex items-center justify-end gap-1 text-xs ${
                        espera >= 20 ? 'font-bold text-rose-600' : 'text-slate-500'
                      }`}
                    >
                      <Clock className="h-3 w-3" />
                      {espera} min
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => accion.onClick(p)}
                  className="btn-primary mt-3 min-h-[44px] w-full justify-center text-sm"
                >
                  {accion.icono}
                  {accion.etiqueta}
                </button>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}
